import { Link, Text } from '@chakra-ui/react';
import { CollabType, collabDashboardHref, type MessageData } from '@fluenceur/types';
import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ugcContentLabel } from '@/pages/collabs/ugc/ugc-tracking-format';
import { LinkedText, readMessageAbout, readMessageDraft } from './LinkedText';
import { MessageAuthorLine, messageAuthorUserId } from './MessageAuthorLine';
import MessageContainer from './MessageContainer';
import { getMessageBubbleRadius, type MessageGroupPosition } from './message-bubble-style';

interface TextMessageProps {
  message: MessageData;
  isSelf: boolean;
  time: string;
  groupPosition?: MessageGroupPosition;
}

/**
 * « À propos de Vidéo 2 · V1 »: the version a creator's reply is about. When
 * the message names its collab, the reference opens the UGC page on that
 * content's review, on that very version.
 */
const AboutReference = ({ about }: { about: NonNullable<ReturnType<typeof readMessageAbout>> }) => {
  const { t } = useTranslation();
  const label = (
    <Text
      data-testid="message-about"
      fontSize="12px"
      fontWeight="600"
      opacity={0.85}
      mb={1}
      ps={2}
      borderStartWidth="2px"
      borderStartColor="currentColor"
    >
      {t('messages.attachment.about', {
        content: ugcContentLabel(t, about),
        version: about.versionNumber,
      })}
    </Text>
  );
  if (!about.collabId) return label;

  const href = collabDashboardHref(
    { id: about.collabId, collabType: CollabType.UGC },
    {
      ugcContent: about.deliverableId ?? undefined,
      ugcVersion: about.deliverableVersionId ?? undefined,
    }
  );
  return <AboutLink href={href}>{label}</AboutLink>;
};

/** The reference as a link into the dashboard, followed without leaving the app. */
const AboutLink = ({ href, children }: { href: string; children: ReactNode }) => {
  const navigate = useNavigate();
  return (
    <Link
      href={href}
      color="inherit"
      textDecoration="none"
      display="block"
      _hover={{ textDecoration: 'underline' }}
      onClick={(event) => {
        event.preventDefault();
        navigate(href);
      }}
    >
      {children}
    </Link>
  );
};

/**
 * Component to render a standard text message.
 */
const TextMessage: React.FC<TextMessageProps> = ({
  message,
  isSelf,
  time,
  groupPosition = 'single',
}) => {
  const { t } = useTranslation();
  if (!message.content) {
    return null;
  }
  // A creator's draft for feedback: a link, named by its content and version. No approval state.
  const draft = readMessageDraft(message.metadata);
  // A creator's reply from the feedback of a UGC content names the version it is about.
  const about = readMessageAbout(message.metadata);

  const isCreatorMessage = !isSelf;
  const authorUserId = isSelf ? messageAuthorUserId(message.metadata) : null;

  return (
    <MessageContainer
      time={time}
      bg={isCreatorMessage ? 'primary.900' : 'softCream'}
      borderColor={isCreatorMessage ? undefined : 'cardBorder'}
      borderWidth={isCreatorMessage ? 0 : '1px'}
      color={isCreatorMessage ? 'primary.50' : 'primary.900'}
      data-message-tone={isCreatorMessage ? 'creator' : 'brand'}
      px={3.5}
      py={2.5}
      rounded="18px"
      {...getMessageBubbleRadius(isCreatorMessage, groupPosition)}
      isSelf={isCreatorMessage}
    >
      {draft ? (
        <Text fontSize="12px" fontWeight="600" opacity={0.85} mb={1}>
          {t('messages.attachment.draft', { format: draft.format, version: draft.version })}
          {' · '}
          {t('messages.attachment.draftSub')}
        </Text>
      ) : null}
      {about ? <AboutReference about={about} /> : null}
      <Text fontSize="14px" lineHeight="20px" whiteSpace="pre-wrap" wordBreak="break-word">
        <LinkedText text={message.content} />
      </Text>
      {authorUserId ? <MessageAuthorLine authorUserId={authorUserId} /> : null}
    </MessageContainer>
  );
};

export default TextMessage;
